/**
 * Sign-in, the second-factor code, and the two screens either side of a
 * forgotten password — inside the SynRank-style frame (login/LoginShell).
 *
 * One component, four modes, no router: the app has no route
 * table. A `?token=` in the URL opens the reset screen directly, which is
 * what the emailed link points at. A sign-in answered with a challenge goes
 * to the CODE mode; no session exists until the code is verified.
 */

import { useState, type FormEvent } from 'react';

import {
  ApiError,
  requestPasswordReset,
  resetPassword,
  signIn,
  verifySignInCode,
  type SessionUser,
} from './api.ts';
import { GoogleButton } from './login/GoogleButton.tsx';
import { WHITE_LABEL_ON } from './whitelabel/switch.ts';
import { usePortalBrand } from './portal/PortalBrand.tsx';
import { COPY, firstMode, tokenFromUrl, type Mode } from './login/login-copy.ts';
import { HumanCheck } from './login/HumanCheck.tsx';
import { LoginShell, PASSWORD_RULES, PasswordChecklist } from './login/LoginShell.tsx';

export function Login({
  onSignedIn,
  initialEmail = '',
  initialNotice = null,
  onStartTrial,
  initialChallenge = '',
}: {
  onSignedIn: (user: SessionUser) => void;
  /** C7 — a Google sign-in that still needs the two-step code. */
  initialChallenge?: string;
  /** Prefilled when arriving from an invitation. */
  initialEmail?: string;
  initialNotice?: string | null;
  /** Opens the free-trial sign-up. */
  onStartTrial?: (() => void) | undefined;
}) {
  const initialToken = tokenFromUrl();
  const [mode, setMode] = useState<Mode>(() => firstMode(initialToken, initialChallenge));
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(initialToken);
  const [confirm, setConfirm] = useState('');
  const [challenge, setChallenge] = useState(initialChallenge);
  const brand = usePortalBrand();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(initialNotice);
  // Turnstile: a token is single-use, so every failed submit asks for a fresh one.
  const [human, setHuman] = useState('');
  const [humanReset, setHumanReset] = useState(0);

  const go = (next: Mode) => {
    setMode(next);
    setError(null);
    setNotice(null);
    setPassword('');
    setConfirm('');
    setCode('');
  };

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : fallback);
      setHumanReset((count) => count + 1);
    } finally {
      setBusy(false);
    }
  };

  const submitSignIn = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      const result = await signIn(email, password, human);
      if ('challengeToken' in result) {
        setChallenge(result.challengeToken);
        go('CODE');
        return;
      }
      onSignedIn(result.user);
    }, 'Sign-in failed. Try again.');
  };

  const submitCode = (event: FormEvent) => {
    event.preventDefault();
    void run(
      async () => onSignedIn(await verifySignInCode(challenge, code)),
      'That code did not work. Try the newest one.',
    );
  };

  const submitForgot = (event: FormEvent) => {
    event.preventDefault();
    // The same wording whether or not the account exists: anything else would
    // let anyone test which addresses are registered.
    void run(async () => {
      await requestPasswordReset(email, human);
      setNotice('If that account exists, an email is on its way. The link lasts one hour.');
    }, 'Could not send the email. Try again.');
  };

  const passwordReady = PASSWORD_RULES.every((rule) => rule.test(password)) && password === confirm;
  const submitReset = (event: FormEvent) => {
    event.preventDefault();
    if (!passwordReady) {
      setError('Choose a password that meets every rule, typed the same twice.');
      return;
    }
    void run(async () => {
      await resetPassword(token.trim(), password, human);
      // Clear the token out of the address bar so it is not left in history.
      window.history.replaceState({}, '', window.location.pathname);
      setToken('');
      go('SIGN_IN');
      setNotice('Password updated. Sign in with the new one.');
    }, 'That link has expired or has already been used.');
  };

  return (
    <LoginShell>
      <header className="li-head">
        <h1>{COPY[mode].title}</h1>
        <p>
          {mode === 'SIGN_IN' && brand
            ? `Sign in to see your ${brand.name} reports.`
            : COPY[mode].blurb}
        </p>
      </header>
      {notice && !error && <div className="li-notice">{notice}</div>}
      {error && (
        <div className="li-error" role="alert">
          {error}
        </div>
      )}

      {mode === 'SIGN_IN' && (
        <form onSubmit={submitSignIn} className="li-form">
          <label htmlFor="email">Email address</label>
          <input
            id="email"
            type="email"
            placeholder="you@company.co.za"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
          <label htmlFor="password">Password</label>
          <input
            id="password"
            type="password"
            placeholder="••••••••"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="current-password"
            required
          />
          <div className="li-forgot">
            <button type="button" className="li-link" onClick={() => go('FORGOT')}>
              Forgot password?
            </button>
          </div>
          <HumanCheck onToken={setHuman} resetKey={humanReset} />
          <button className="li-submit" type="submit" disabled={busy || !human}>
            {busy ? 'Signing in…' : 'Sign in →'}
          </button>
          {WHITE_LABEL_ON && <GoogleButton />}
          {onStartTrial && (
            <p className="li-after">
              Don't have an account?{' '}
              <button type="button" className="li-link" onClick={onStartTrial}>
                Start a free trial
              </button>
            </p>
          )}
        </form>
      )}

      {mode === 'CODE' && (
        <form onSubmit={submitCode} className="li-form">
          <label htmlFor="code">Authenticator code</label>
          <input
            id="code"
            className="li-code"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            placeholder="000000"
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            autoFocus
          />
          <button className="li-submit" type="submit" disabled={busy || code.length < 6}>
            {busy ? 'Verifying…' : 'Verify →'}
          </button>
          <p className="li-after">
            <button type="button" className="li-link" onClick={() => go('SIGN_IN')}>
              ← Back to sign in
            </button>
          </p>
        </form>
      )}

      {mode === 'FORGOT' && (
        <form onSubmit={submitForgot} className="li-form">
          <label htmlFor="forgot-email">Email address</label>
          <input
            id="forgot-email"
            type="email"
            placeholder="you@company.co.za"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            autoComplete="email"
            required
            autoFocus
          />
          <HumanCheck onToken={setHuman} resetKey={humanReset} />
          <button className="li-submit" type="submit" disabled={busy || !human}>
            {busy ? 'Sending…' : 'Send reset link →'}
          </button>
          <p className="li-after">
            <button type="button" className="li-link" onClick={() => go('RESET')}>
              I already have a token
            </button>
            {' · '}
            <button type="button" className="li-link" onClick={() => go('SIGN_IN')}>
              Back to sign in
            </button>
          </p>
        </form>
      )}

      {mode === 'RESET' && (
        <form onSubmit={submitReset} className="li-form">
          <label htmlFor="reset-token">Reset token</label>
          <input
            id="reset-token"
            type="text"
            value={token}
            onChange={(event) => setToken(event.target.value)}
            autoComplete="off"
            spellCheck={false}
            required
          />
          <label htmlFor="new-password">New password</label>
          <input
            id="new-password"
            type="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            autoComplete="new-password"
            required
          />
          <label htmlFor="confirm-password">Retype new password</label>
          <input
            id="confirm-password"
            type="password"
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
            autoComplete="new-password"
            required
          />
          <PasswordChecklist value={password} confirm={confirm} />
          <HumanCheck onToken={setHuman} resetKey={humanReset} />
          <button className="li-submit" type="submit" disabled={busy || !passwordReady || !human}>
            {busy ? 'Updating…' : 'Update password →'}
          </button>
          <p className="li-after">
            <button type="button" className="li-link" onClick={() => go('SIGN_IN')}>
              Back to sign in
            </button>
          </p>
        </form>
      )}
    </LoginShell>
  );
}
